/**
 * Map dashboard pathname → One AI page context (module awareness).
 */

export type OneAiPageModule =
  | "home"
  | "crm"
  | "finance"
  | "marketing"
  | "mail"
  | "knowledge"
  | "projects"
  | "hr"
  | "support"
  | "organization"
  | "users"
  | "platform"
  | "pay"
  | "analytics"
  | "sso"
  | "other";

export type OneAiPageContext = {
  path: string;
  module: OneAiPageModule;
  /** Short label for prompts / UI */
  label: string;
  /** Default suggestion chips for this surface */
  suggestions: string[];
  /** System-prompt guidance: prefer this module unless user pivots */
  promptHint: string;
};

function stripOrgPrefix(pathname: string): string {
  // /app/org/4133643/dashboard/crm/... → /dashboard/crm/...
  const orgMatch = pathname.match(/^\/app\/org\/[^/]+(\/.*)?$/);
  if (orgMatch) return orgMatch[1] || "/";
  return pathname;
}

/** Map peeled one.atw.net paths to dashboard-style paths for rule matching. */
function normalizePathForRules(path: string): string {
  if (path.startsWith("/dashboard")) return path;
  if (path === "/" || path === "") return "/dashboard";
  return `/dashboard${path}`;
}

export function resolveOneAiPageContext(pathname: string | null | undefined): OneAiPageContext {
  const raw = String(pathname || "").trim() || "/";
  const stripped = stripOrgPrefix(raw).split("?")[0] || "/";
  const path = normalizePathForRules(stripped);

  const rules: Array<{ test: RegExp; module: OneAiPageModule; label: string; suggestions: string[]; promptHint: string }> = [
    {
      test: /\/dashboard\/crm(\/|$)/,
      module: "crm",
      label: "CRM",
      suggestions: ["Create a lead", "List my leads", "Train from this page"],
      promptHint:
        "The user is on CRM. Prefer leads, contacts, accounts, deals. Use list_crm_leads research when they ask what’s in CRM.",
    },
    {
      test: /\/dashboard\/finance(\/|$)/,
      module: "finance",
      label: "Finance",
      suggestions: ["List customers and invoices", "Create a customer and invoice", "Train from this page"],
      promptHint:
        "The user is on Finance. Prefer customers, invoices, payments. Use list_finance_customers research when they ask what’s in Finance.",
    },
    {
      test: /\/dashboard\/(marketing|blogs)(\/|$)/,
      module: "marketing",
      label: "Marketing",
      suggestions: ["Help with a campaign", "What can Marketing do?", "Draft a social post idea"],
      promptHint:
        "The user is on Marketing (campaigns, blogs, or socials). Prefer campaign/content help. Do not open with CRM lead/invoice prompts unless they ask or the thread is already about that.",
    },
    {
      test: /\/dashboard\/knowledge(\/|$)/,
      module: "knowledge",
      label: "Knowledge",
      suggestions: ["Find an article", "Open a library", "What should we document?"],
      promptHint:
        "The user is on Knowledge. Prefer articles, libraries, and books. A book is a collection key on an article. Do not assign articles to agents from this screen.",
    },
    {
      test: /\/dashboard\/projects(\/|$)/,
      module: "projects",
      label: "Projects",
      suggestions: ["Create a project", "List open tasks", "Log an issue"],
      promptHint:
        "The user is on Projects. Prefer projects, tasks (planned work), and issues (bugs/questions/errors). Distinct from Support tickets and Knowledge wiki.",
    },
    {
      test: /\/dashboard\/human-resources(\/|$)/,
      module: "hr",
      label: "Human Resources",
      suggestions: ["Add an employee", "How does the directory work?"],
      promptHint: "The user is on HR. Prefer directory/employee help.",
    },
    {
      test: /\/dashboard\/support(\/|$)/,
      module: "support",
      label: "Support tickets",
      suggestions: ["Create a ticket", "How do Support tickets work?"],
      promptHint: "The user is on Support tickets. Prefer ticket workflows — distinct from One AI chat itself.",
    },
    {
      test: /\/dashboard\/(organization|products)(\/|$)|#products/,
      module: "organization",
      label: "Organization",
      suggestions: ["Enable ATW Mail", "Explain Credits", "How does the trial work?"],
      promptHint: "The user is on Organization / Products. Prefer plan, products (Mail/Pay/SSO), and trial/Credits.",
    },
    {
      test: /\/dashboard\/users(\/|$)/,
      module: "users",
      label: "Users",
      suggestions: ["Invite a teammate", "Explain roles"],
      promptHint: "The user is on Users. Prefer invites and roles.",
    },
    {
      test: /\/dashboard\/platform(\/|$)/,
      module: "platform",
      label: "Platform",
      suggestions: ["What is Platform for?", "Check system status"],
      promptHint: "The user is on the ATW Platform operator area.",
    },
    {
      test: /\/dashboard\/pay(\/|$)/,
      module: "pay",
      label: "Pay",
      suggestions: ["How does ATW Pay work?", "Enable Pay"],
      promptHint: "The user is on Pay. Prefer payments product help — never reveal live secrets.",
    },
    {
      test: /\/dashboard\/analytics(\/|$)/,
      module: "analytics",
      label: "Analytics",
      suggestions: ["How do website journeys work?", "Where do I add a site?", "How do leads link?"],
      promptHint:
        "The user is on Analytics. Prefer sites, write keys, anonymous visitor/session tracking, and CRM journey links. Do not invent GA4 admin steps.",
    },
    {
      test: /\/dashboard\/sso(\/|$)/,
      module: "sso",
      label: "SSO",
      suggestions: ["How does SSO work?", "Enable SSO"],
      promptHint: "The user is on SSO. Prefer IdP/client setup — never reveal private keys.",
    },
    {
      test: /\/(mail-app|mail-admin|mail)(\/|$)/,
      module: "mail",
      label: "ATW Mail",
      suggestions: ["Help with inbox", "How do I add a mailbox?"],
      promptHint: "The user is in ATW Mail. Prefer mailbox/admin help — never reveal mailbox passwords.",
    },
    {
      test: /\/dashboard\/?$/,
      module: "home",
      label: "Home",
      suggestions: ["Winning customers", "Getting paid", "How does the trial work?"],
      promptHint: "The user is on Home / Getting Started. A light first-job nudge is OK.",
    },
  ];

  for (const rule of rules) {
    if (rule.test.test(path)) {
      return {
        path,
        module: rule.module,
        label: rule.label,
        suggestions: rule.suggestions,
        promptHint: rule.promptHint,
      };
    }
  }

  return {
    path,
    module: "other",
    label: "ATW One",
    suggestions: ["What can you help with?", "How does the trial work?"],
    promptHint: "Stay helpful and ask which area they care about if unclear. Avoid random CRM prompts.",
  };
}

/** Hours after which reopening One AI gets a welcome-back line. */
export const ONE_AI_WELCOME_BACK_HOURS = 4;

export function shouldWelcomeBack(lastActiveAtIso: string | null | undefined, nowMs = Date.now()): boolean {
  if (!lastActiveAtIso) return false;
  const t = Date.parse(lastActiveAtIso);
  if (!Number.isFinite(t)) return false;
  return nowMs - t >= ONE_AI_WELCOME_BACK_HOURS * 60 * 60 * 1000;
}

export function welcomeBackReply(page: OneAiPageContext): string {
  return `Welcome back. You're on **${page.label}** — what can I help with?`;
}
